import { useState } from 'react';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { useChannelClaudeToken, useSetChannelClaudeToken } from '../../hooks/useBotAdmin';

/**
 * Phase 78 (Plan 78-05 / D-03 gap-closure) — the shared channel-service
 * Claude token: set by an admin via this field, dispensed over the existing
 * HMAC-gated channel-credentials endpoint to the fork. Structural clone of
 * `KillSwitchToggle.tsx`'s card shell.
 *
 * Show-once semantics: the input always starts (and, on save success,
 * returns to) empty — the current stored value is never rendered, and the
 * status badge below is boolean-only (present/absent), never the value.
 *
 * MKS-275: this is the TopMonks Robot's Claude seat, and since 2026-10-13 the
 * shared Factory™ agents run on it too — which is why the card says so: setting
 * the token here rotates `ROBOT_CLAUDE_TOKEN` in every bound Factory™ company as
 * well as serving the Slack channel bot.
 */
export function ChannelClaudeTokenField() {
  const { data, isLoading, error } = useChannelClaudeToken();
  const setTokenMutation = useSetChannelClaudeToken();
  const [token, setToken] = useState('');
  const [unverified, setUnverified] = useState(false);

  const present = data?.present ?? false;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setUnverified(false);
    setTokenMutation.mutate(token, {
      // Store-and-warn: the token is always saved; `verified: false` means the
      // best-effort verify call didn't confirm it (Pitfall 6), not a failure.
      onSuccess: (result) => {
        setToken('');
        setUnverified(result.verified === false);
      },
    });
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-900">TopMonks Robot Claude token</p>
          <p className="text-xs mt-0.5 text-gray-500">
            Used by the Slack channel bot <span className="font-medium">and</span> the shared
            Factory&trade; agents (Reviewer, Triager, Gardener).
          </p>
          <p className="text-sm mt-0.5">
            {isLoading ? (
              <span className="text-gray-400">Loading...</span>
            ) : error ? (
              <span className="text-red-600">Failed to load token status.</span>
            ) : present ? (
              <span aria-label="Channel Claude token present" className="text-green-600 font-medium">
                Token set
              </span>
            ) : (
              <span aria-label="Channel Claude token absent" className="text-gray-500 font-medium">
                No token
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="mt-2 rounded-md bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-600">
        <p className="font-medium text-gray-700">How to generate this token</p>
        <p className="mt-1">
          This is the shared Claude account the TopMonks Robot runs under (not a personal one).
          Signed in as the shared account:
        </p>
        <ol className="mt-1 list-decimal list-inside space-y-0.5">
          <li>
            Install <span className="font-medium">Claude Code</span>.
          </li>
          <li>
            Run <code className="font-mono bg-gray-100 px-1 rounded">claude setup-token</code> and
            complete the browser login as the shared account.
          </li>
          <li>
            Copy the <code className="font-mono bg-gray-100 px-1 rounded">sk-ant-oat...</code> value
            it prints and paste it below.
          </li>
        </ol>
        <p className="mt-1 text-gray-500">
          Stored encrypted and never shown again. Paste a fresh token here to rotate it. Rotating it
          here also syncs it to every bound Factory&trade; company — watch the Paperclip tab for the
          result.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-3 flex items-end gap-2">
        <div className="flex-1">
          <Input
            id="channel-claude-token"
            label="Set / replace token"
            type="password"
            autoComplete="off"
            placeholder="sk-ant-oat..."
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
        </div>
        <Button
          type="submit"
          disabled={!token || setTokenMutation.isPending}
          isLoading={setTokenMutation.isPending}
        >
          Save
        </Button>
      </form>

      {setTokenMutation.isError && (
        <p className="mt-2 text-sm text-red-600">
          {(setTokenMutation.error as Error).message}
        </p>
      )}

      {unverified && !setTokenMutation.isError && (
        <p className="mt-2 text-sm text-amber-600">
          Saved, but we couldn&apos;t verify the token just now. It may still work — the check is
          best-effort. If the channel bot can&apos;t reply, paste a fresh token here.
        </p>
      )}
    </div>
  );
}
